/**
 * URL-carried back navigation (z8nrz7czdw).
 *
 * A detail page reachable from several lists cannot know which one sent it.
 * `/procedure-templates/:id/sequence` is the worked example: `ProcedureTemplatesPage`
 * renders from three places — the standalone `/procedure-templates` route and
 * BOTH the `templates` and `process` tabs of `/form-builder` — so a hardcoded
 * "back" is wrong two times in three. The origin travels in the query string:
 *
 *   /procedure-templates/abc/sequence?from=%2Fform-builder%3Ftab%3Dtemplates&fromLabel=Procedure+Templates
 *
 * Why the URL and not router state: `StorageUnitView` solves the same problem
 * with `location.state.from` and its own comment records the cost — "a reload
 * loses that state". A query parameter survives a reload, a shared link and a
 * new tab. That page is deliberately left alone (out of scope); this is the
 * mechanism new callers use.
 *
 * ── The security shape ──────────────────────────────────────────────────────
 *
 * `from` is a raw path rather than a key into a registry, which means it is
 * USER-EDITABLE and cannot be trusted. Everything here exists to make an
 * attacker-supplied value inert: a bad value renders no control at all, rather
 * than a control that leaves the app. `isSafeInternalPath` is the only gate —
 * never build a link from a raw `from` without it.
 *
 * Pure (no React, no router imports) so `vitest.config.ts` — `environment: node`,
 * `include: ['src/**\/*.test.ts']` — can actually cover it; the same reasoning
 * that keeps logic out of `.tsx` everywhere else in this repo.
 */

/** Query keys. Exported so callers and tests never spell them by hand. */
export const BACK_NAV_PARAM = 'from'
export const BACK_NAV_LABEL_PARAM = 'fromLabel'

/**
 * Caps. `from` is generous because a legitimate origin carries its own query
 * (and, when chained, an encoded `from` of its own). `fromLabel` is short: it
 * renders on one line next to an arrow, and anything longer is not a label.
 */
export const MAX_FROM_LENGTH = 512
export const MAX_LABEL_LENGTH = 40

/** Shown when a valid origin carries no usable label. */
export const DEFAULT_BACK_LABEL = 'Back'

/** Anything C0/C1 or a Unicode line separator. */
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F\u2028\u2029]/

/**
 * True when `value` is a path this app may navigate to.
 *
 * The rejections, and why each one is here rather than assumed:
 *
 * - **not a single leading `/`** — the value must be app-relative. `https://evil.com`
 *   and a bare `evil.com` both fail here.
 * - **`//` prefix** — protocol-relative. `//evil.com` IS a leading slash and
 *   navigates off-site; a `startsWith('/')` check alone lets it straight through,
 *   which is the classic form of this bug.
 * - **any backslash** — browsers normalise `\` to `/`, so `/\evil.com` becomes
 *   `//evil.com` after the check would have passed it.
 * - **control characters** — `/\t/evil.com` and friends are stripped by URL
 *   parsers, changing the value after validation.
 * - **over the cap** — a link nobody authored.
 *
 * A scheme cannot appear at the front once a leading `/` is required, so
 * `javascript:` is unreachable as a destination; it is still rejected by the
 * backslash/control rules if smuggled, and a literal `/javascript:alert(1)` is
 * merely a route that matches nothing.
 */
export function isSafeInternalPath(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false
  if (value.length === 0 || value.length > MAX_FROM_LENGTH) return false
  if (CONTROL_CHARS.test(value)) return false
  if (value.includes('\\')) return false
  if (!value.startsWith('/')) return false
  if (value.startsWith('//')) return false
  return true
}

/** A label safe to render: trimmed, capped, never empty. */
export function normalizeBackLabel(value: string | null | undefined): string {
  if (typeof value !== 'string') return DEFAULT_BACK_LABEL
  if (CONTROL_CHARS.test(value)) return DEFAULT_BACK_LABEL
  const trimmed = value.trim()
  if (!trimmed || trimmed.length > MAX_LABEL_LENGTH) return DEFAULT_BACK_LABEL
  return trimmed
}

export interface BackNavTarget {
  /** Where the control navigates. Already validated. */
  to: string
  /** What it reads. Never empty. */
  label: string
}

/**
 * The back target carried by a URL's query, or null when there is none to
 * honour.
 *
 * Null is the "render nothing" signal, and it covers a rejected value as well
 * as an absent one — a page opened with a hostile `from` must look exactly like
 * one opened with no `from`, not like one with a broken button.
 *
 * Takes a `URLSearchParams` rather than a router hook so this stays testable
 * outside React.
 */
export function parseBackNav(params: URLSearchParams): BackNavTarget | null {
  const from = params.get(BACK_NAV_PARAM)
  if (!isSafeInternalPath(from)) return null
  return { to: from, label: normalizeBackLabel(params.get(BACK_NAV_LABEL_PARAM)) }
}

/**
 * `path` with the back-nav parameters appended.
 *
 * Existing query on `path` is preserved: callers pass targets that already
 * carry state (a tab, a filter), and dropping it would trade one navigation bug
 * for another. An unsafe `origin` is ignored, so a caller cannot mint a link
 * this module's own parser would then refuse.
 */
export function withBackNav(path: string, origin: string, label?: string): string {
  if (!isSafeInternalPath(origin)) return path

  const hashAt = path.indexOf('#')
  const hash = hashAt === -1 ? '' : path.slice(hashAt)
  const withoutHash = hashAt === -1 ? path : path.slice(0, hashAt)

  const queryAt = withoutHash.indexOf('?')
  const base = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt)
  const params = new URLSearchParams(queryAt === -1 ? '' : withoutHash.slice(queryAt + 1))

  params.set(BACK_NAV_PARAM, origin)
  const normalized = normalizeBackLabel(label)
  if (normalized !== DEFAULT_BACK_LABEL) params.set(BACK_NAV_LABEL_PARAM, normalized)
  else params.delete(BACK_NAV_LABEL_PARAM)

  return `${base}?${params.toString()}${hash}`
}

/**
 * The `from` value describing the page the user is on right now — pathname plus
 * its own query.
 *
 * This is what makes chains work. Going Templates → sequence editor → form
 * editor, the form editor's `from` must be the sequence editor's FULL url
 * including the sequence editor's own `from`, or the second back press lands on
 * a sequence editor with no way out. `withBackNav` percent-encodes it, so the
 * inner `from` simply survives as part of the value.
 */
export function currentPathWithQuery(location: { pathname: string; search: string }): string {
  return `${location.pathname}${location.search}`
}
